import { jsx, jsxs } from "react/jsx-runtime";
import { ThemeProvider, Box } from "@mui/material";
import MuiMenuItem from "@mui/material/MenuItem/index.js";
import { createTheme } from "../../constants/create-theme/create-theme.js";
import "../../themes/typography/typography.js";
import { MuiButton } from "../../themes/button/button.js";
import { MuiCheckbox } from "../../themes/checkbox/checkbox.js";
import "../../themes/external-theme/external-theme.js";
import "color-alpha";
import "../../themes/pickers-popper/pickers-popper.js";
import "../../themes/shadows/shadows.js";
import { MuiSwitch } from "../../themes/switch/switch.js";
import { MenuItemCounter } from "./components/menu-item-counter/menu-item-counter.js";
import { menuItemStyles, ContentWrapper, StyledCheckbox, StyledAvatar, StyledIcon, StyledSubtitle, StyledSwitch, StyledCheckmarkIcon } from "./menu-item.styles.js";
import "@mui/material/styles";
import "../../constants/colors/palette.js";
import "../../themes/typography/utils/defaultVariantMapping/defaultVariantMapping.js";
import "../../themes/typography/utils/typographyVariants/typographyVariants.js";
import "../../themes/button/styles/external/bordered.styles.js";
import "../../themes/button/styles/external/filled.styles.js";
import "../../themes/button/styles/internal/bordered.styles.js";
import "../../themes/button/styles/internal/borderless.styles.js";
import "../../themes/button/styles/internal/filled.styles.js";
import "../../themes/button/styles/internal/shadowed.styles.js";
import "../../themes/checkbox/components/CheckedIcon/CheckedIcon.js";
import "@mui/material/SvgIcon/index.js";
import "../../themes/checkbox/components/IndeterminateIcon/IndeterminateIcon.js";
import "../../constants/breakpoints/breakpoints.js";
import "../../themes/alert/alert.js";
import "../../themes/alert/components/ErrorIcon/ErrorIcon.js";
import "../../themes/alert/components/InfoIcon/InfoIcon.js";
import "../../themes/alert/components/SuccessIcon/SuccessIcon.js";
import "../../themes/alert/components/WarningIcon/WarningIcon.js";
import "../../themes/form-control-label/form-control-label.js";
import "../../themes/radio/radio.js";
import "react";
import "./assets/icons/minus-circle.js";
import "./assets/icons/plus-circle.js";
import "./components/menu-item-counter/menu-item-counter.styles.js";
import "../avatar/avatar.js";
import "@mui/material/Avatar/index.js";
import "../avatar/avatar.styles.js";
import "../icons/MainIcon/MainIcon.js";
const getNameInitials = (name = "", maxInitials) => {
  return name.trim().split(/\s+/).reduce((initials, word, index) => {
    if (!maxInitials || index < maxInitials) {
      initials.push(word.charAt(0).toUpperCase());
    }
    return initials;
  }, []).join("");
};
const theme = createTheme({
  components: {
    MuiSwitch,
    MuiCheckbox,
    MuiButton
  }
});
const MenuItem = ({
  size = "m",
  variant,
  avatar,
  children,
  icon,
  subtitle,
  checkboxPlacement = "after",
  onCounterChange,
  value,
  maxValue,
  counterStep = 1,
  disabledCounter,
  counterValue,
  ...rest
}) => /* @__PURE__ */ jsx(ThemeProvider, { theme, children: /* @__PURE__ */ jsxs(MuiMenuItem, { disableRipple: true, disableTouchRipple: true, sx: menuItemStyles({ size }), value, ...rest, children: [
  /* @__PURE__ */ jsxs(ContentWrapper, { children: [
    (variant === "checkbox" && checkboxPlacement === "before" || variant === "checkbox-counter") && size !== "xs" && /* @__PURE__ */ jsx(Box, { mr: 1, children: /* @__PURE__ */ jsx(
      StyledCheckbox,
      {
        checked: rest.selected,
        size: "small",
        disableRipple: true,
        disableFocusRipple: true,
        disableTouchRipple: true,
        readOnly: true
      }
    ) }),
    avatar && size !== "xs" && !icon && /* @__PURE__ */ jsxs(StyledAvatar, { src: avatar.imageUrl, size: "m", children: [
      avatar.Icon,
      !avatar.Icon && getNameInitials(avatar.userName ?? `${avatar.firstName} ${avatar.lastName}`)
    ] }),
    icon && /* @__PURE__ */ jsx(StyledIcon, { size: "md", children: icon }),
    /* @__PURE__ */ jsx("span", { children })
  ] }),
  /* @__PURE__ */ jsxs(ContentWrapper, { children: [
    subtitle && size === "m" && /* @__PURE__ */ jsx(StyledSubtitle, { variant: "Lato Emphasized 2", component: "span", children: subtitle }),
    variant === "checkbox" && size !== "xs" && checkboxPlacement === "after" && /* @__PURE__ */ jsx(
      StyledCheckbox,
      {
        checked: rest.selected,
        size: "small",
        disableRipple: true,
        disableFocusRipple: true,
        disableTouchRipple: true,
        readOnly: true
      }
    ),
    variant === "switch" && /* @__PURE__ */ jsx(
      StyledSwitch,
      {
        checked: rest.selected,
        disableRipple: true,
        disabled: true,
        disableFocusRipple: true,
        disableTouchRipple: true,
        ...size !== "l" && {
          size: "small"
        }
      }
    ),
    variant === "checkmark" && rest.selected && /* @__PURE__ */ jsx(StyledCheckmarkIcon, { size: "md", children: /* @__PURE__ */ jsx("path", { d: "M19.9805 5.99023C19.7207 5.99798 19.4741 6.10655 19.293 6.29297L9.00002 16.5859L5.70705 13.293C5.61491 13.197 5.50454 13.1204 5.38241 13.0676C5.26028 13.0148 5.12885 12.9869 4.99581 12.9856C4.86277 12.9842 4.7308 13.0094 4.60762 13.0597C4.48444 13.11 4.37254 13.1844 4.27846 13.2784C4.18438 13.3725 4.11002 13.4844 4.05973 13.6076C4.00944 13.7308 3.98423 13.8627 3.98559 13.9958C3.98694 14.1288 4.01483 14.2603 4.06761 14.3824C4.1204 14.5045 4.19702 14.6149 4.29299 14.707L8.29299 18.707C8.48054 18.8945 8.73485 18.9998 9.00002 18.9998C9.26519 18.9998 9.51951 18.8945 9.70705 18.707L20.7071 7.70703C20.8516 7.56657 20.9503 7.38573 20.9902 7.18821C21.0302 6.99069 21.0095 6.7857 20.931 6.60012C20.8524 6.41453 20.7197 6.257 20.5501 6.14817C20.3805 6.03935 20.1819 5.98429 19.9805 5.99023Z" }) }),
    (variant === "checkbox-counter" || variant === "counter") && /* @__PURE__ */ jsx(
      MenuItemCounter,
      {
        value: counterValue ?? 0,
        onChange: onCounterChange,
        max: maxValue,
        disabled: disabledCounter,
        step: counterStep
      }
    )
  ] })
] }) });
export {
  MenuItem,
  getNameInitials
};
